import { NETWORK_SUMMARY, NETWORK_FOUNDING, NETWORK_SEPARATE, NETWORK_TAGLINE } from "@/lib/network";
import { createFileRoute } from "@tanstack/react-router";
import { Section, SectionHeading, CTASection } from "@/components/layout";
import { DevSafeForm } from "@/components/dev-form";
import { KIT_TAGS } from "@/lib/kit.functions";

const TITLE = "Tables and Bridges Podcast | Community Engagement Conversations";
const DESC =
  "A live audio experience for law enforcement Community Engagement professionals. Coming November 1, 2026.";


export const Route = createFileRoute("/podcast")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: "https://tablesandbridges.com/podcast" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://tablesandbridges.com/podcast" }],
  }),
  component: PodcastPage,
});

function PodcastPage() {
  return (
    <div>
      {/* HERO */}
      <section className="relative bg-navy text-warm-white overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-25">
          <svg className="w-full h-full" viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice" fill="none">
            {[...Array(40)].map((_, i) => (
              <rect
                key={i}
                x={40 + i * 34}
                y={280 + Math.sin(i * 0.5) * 90}
                width="6"
                height={80 + Math.abs(Math.sin(i * 0.7)) * 140}
                fill="#F2B84B"
                fillOpacity={0.35 + (i % 5) * 0.08}
              />
            ))}
          </svg>
        </div>
        <div className="container-editorial relative z-10 pt-28 pb-24 md:pt-36 md:pb-32">
          <p className="eyebrow mb-8">Tables and Bridges, Live</p>
          <h1 className="max-w-5xl font-display text-[clamp(2.5rem,5.6vw,5rem)] leading-[1] tracking-[-0.02em]">
            Before the podcast, the conversation{" "}
            <span className="italic text-gold">goes live.</span>
          </h1>
          <p className="mt-10 max-w-3xl text-warm-white/85 text-lg leading-relaxed">
            Tables and Bridges is opening a live audio experience for Community
            Engagement professionals. Real conversations, in real time, with the
            people doing the work. What you hear, ask, and bring will shape what
            the podcast becomes.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a href="#updates" className="btn-base btn-primary">Get Notified</a>
            <a href="https://ceopractitionernetwork.com" target="_blank" rel="noopener noreferrer" className="btn-base btn-secondary-dark">Explore the CEO Practitioner Network</a>
            <div className="border-l border-warm-white/20 pl-4">
              <p className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold">
                Coming
              </p>
              <p className="text-warm-white/90 mt-1">November 1, 2026</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT TO EXPECT */}
      <Section tone="warm-white">
        <SectionHeading eyebrow="What to expect" title="Field-first, and in real time." />
        <ul className="mt-10 grid gap-4 md:grid-cols-2 max-w-4xl text-navy/85">
          {[
            "Live conversations with Community Engagement practitioners and leaders",
            "Room to listen, ask questions, and bring your own field challenges",
            "Supervisor, command, and community perspectives",
            "Honest talk about what builds trust, what breaks it, and what makes it hold",
            "Topics shaped by the people who show up",
          ].map((c) => (
            <li key={c} className="flex gap-3 border-t border-navy/10 pt-4">
              <span aria-hidden className="text-gold">▸</span>
              {c}
            </li>
          ))}
        </ul>
      </Section>

      {/* WHY LIVE FIRST */}
      <Section tone="ink">
        <SectionHeading
          eyebrow="Why live first"
          title="We want to hear from the field before we build the archive."
        />
        <p className="mt-8 max-w-3xl text-warm-white/85 text-lg leading-relaxed">
          The live experience lets practitioners shape the conversation from the
          start. It also tells us what the podcast should become, so what we
          publish next is built on what the field actually needs.
        </p>
      </Section>

      {/* LIVE TO CEO PRACTITIONER NETWORK */}
      <Section tone="deep-green">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_1fr] items-center">
          <div>
            <p className="eyebrow mb-5">Listen, then continue</p>
            <h2 className="font-display">
              Join the conversation live.
              <br />
              <span className="italic text-gold">Continue it inside the CEO Practitioner Network.</span>
            </h2>
            <p className="mt-8 text-warm-white/85 leading-relaxed text-lg measure-body">
              The live experience introduces ideas and brings the field into conversation.
              {NETWORK_TAGLINE} {NETWORK_SUMMARY} {NETWORK_FOUNDING} {NETWORK_SEPARATE}
            </p>
          </div>
          <div className="lg:justify-self-end">
            <a href="https://ceopractitionernetwork.com" target="_blank" rel="noopener noreferrer" className="btn-base btn-primary">Explore the CEO Practitioner Network</a>
          </div>
        </div>
      </Section>

      {/* UPDATES FORM */}
      <Section tone="navy" id="updates">
        <div className="grid gap-12 lg:grid-cols-[1fr_1.3fr] items-start">
          <div>
            <p className="eyebrow mb-5">Launch updates</p>
            <h2 className="font-display">Be there November 1.</h2>
            <span aria-hidden className="divider-gold mt-6 mb-6" />
            <p className="text-warm-white/80 leading-relaxed">
              Get notified when the live experience opens, with the date, time,
              and how to join.
            </p>
          </div>
          <div className="border border-warm-white/12 bg-warm-white/5 p-6 md:p-10">
            <DevSafeForm
              formName="podcast-updates"
              kitTag={KIT_TAGS.podcastUpdates}
              tone="onNavy"
              submitLabel="Get Notified"
              successMessage="You are on the list. We will send the details before November 1."
              fields={[
                { name: "first_name", label: "First name", required: true },
                { name: "last_name", label: "Last name" },
                { name: "email", label: "Email", type: "email", required: true, full: true },
                { name: "role", label: "Your role", optional: true },
              ]}
            />
          </div>
        </div>
      </Section>

      <CTASection
        title="The field deserves its own conversation."
        primary={{ label: "Get Notified", href: "#updates" }}
        secondary={{ label: "Explore the CEO Practitioner Network", href: "https://ceopractitionernetwork.com", external: true }}
        tone="ink"
      />
    </div>
  );
}
